/**
 * Daily Streak Component
 * VOY-133: Consecutive play rewards system
 */

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Flame, Gift, Lock } from 'lucide-react';

interface StreakMilestone {
  days: number;
  reward: string;
  unlocked: boolean;
}

interface DailyStreakProps {
  currentStreak: number;
  todayPlayed: boolean;
  nextReward: string;
  milestones: StreakMilestone[];
}

export function DailyStreak({ currentStreak, todayPlayed, nextReward, milestones }: DailyStreakProps) {
  // Find next locked milestone
  const nextMilestone = milestones.find(m => !m.unlocked);
  const prevMilestone = [...milestones].reverse().find(m => m.unlocked);
  
  const startValue = prevMilestone ? prevMilestone.days : 0;
  const targetValue = nextMilestone ? nextMilestone.days : milestones[milestones.length - 1].days;
  const progress = nextMilestone 
    ? ((currentStreak - startValue) / (targetValue - startValue)) * 100 
    : 100;

  return (
    <Card className="rounded-3xl bg-gradient-to-r from-orange-500 to-red-500 text-white border-0 shadow-xl">
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <Flame className="w-7 h-7 text-orange-200" />
            </div>
            <div>
              <h3 className="text-2xl font-bold">{currentStreak} Day Streak</h3>
              <p className="text-white/80 text-sm">Keep it going!</p>
            </div>
          </div>
          {todayPlayed && (
            <div className="bg-green-500 px-3 py-1 rounded-full text-sm font-semibold">
              ✅ Played Today
            </div>
          )}
        </div>

        {/* Progress to Next Reward */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-white/90">Progress to next reward</span>
            <span className="font-semibold">
              {nextMilestone ? `${currentStreak}/${nextMilestone.days} days` : 'Max reached!'}
            </span>
          </div>
          <div className="w-full bg-white/20 rounded-full h-3 overflow-hidden">
            <div
              className="h-full bg-white transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Next Reward */}
        <div className="bg-white/10 rounded-xl p-3 mb-4">
          <p className="text-sm text-white/80 mb-1">Tomorrow's Reward:</p>
          <p className="font-semibold">{nextReward}</p>
        </div>

        {/* Milestones */}
        <div>
          <p className="text-sm font-semibold mb-3">Streak Milestones:</p>
          <div className="grid grid-cols-2 gap-2">
            {milestones.map((milestone) => (
              <div
                key={milestone.days}
                className={`flex items-center gap-2 p-2 rounded-lg ${
                  milestone.unlocked ? 'bg-green-500/30' : 'bg-white/10'
                }`}
              >
                {milestone.unlocked ? (
                  <Gift className="w-4 h-4 text-green-200" />
                ) : (
                  <Lock className="w-4 h-4 text-white/50" />
                )}
                <div>
                  <p className="text-xs font-semibold">{milestone.days} days</p>
                  <p className="text-xs text-white/80">{milestone.reward}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        {!todayPlayed && (
          <Button
            className="w-full mt-4 bg-white text-orange-600 hover:bg-white/90 font-semibold rounded-full"
          >
            Play Today to Keep Streak! 🔥
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

